import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useCreateArticleCollection } from '@/queries/article-collections';
import CloseIcon from '@/assets/common/close.svg?react';
import styles from './CreateCollectionModal.module.scss';

interface CreateCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (collectionId: string) => void;
}

export function CreateCollectionModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateCollectionModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createCollection = useCreateArticleCollection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Collection name is required');
      return;
    }

    try {
      const result = await createCollection.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });

      setName('');
      setDescription('');
      onClose();

      if (onSuccess) {
        onSuccess(result.collection.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create collection');
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setError(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.createCollectionModal__overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className={styles.createCollectionModal__content}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.createCollectionModal__header}>
              <h2 className={styles.createCollectionModal__title}>
                Create Collection
              </h2>
              <button
                className={styles.createCollectionModal__closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className={styles.createCollectionModal__body}>
                <div className={styles.createCollectionModal__field}>
                  <label className={styles.createCollectionModal__label}>
                    Name
                  </label>
                  <input
                    type="text"
                    className={styles.createCollectionModal__input}
                    placeholder="Enter collection name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                  />
                </div>

                <div className={styles.createCollectionModal__field}>
                  <label className={styles.createCollectionModal__label}>
                    Description (optional)
                  </label>
                  <textarea
                    className={styles.createCollectionModal__textarea}
                    placeholder="Enter a description for this collection"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                {error && (
                  <div className={styles.createCollectionModal__error}>
                    {error}
                  </div>
                )}
              </div>

              <div className={styles.createCollectionModal__footer}>
                <button
                  type="button"
                  className={`${styles.createCollectionModal__button} ${styles['createCollectionModal__button--secondary']}`}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${styles.createCollectionModal__button} ${styles['createCollectionModal__button--primary']}`}
                  disabled={createCollection.isPending}
                >
                  {createCollection.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
